import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListAccountAddressesResult } from '@whinerp/client';
import { Button, Field, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';

/**
 * A company's addresses (G-033): billing, shipping, sites and branches.
 *
 * Each kind says which address is its default, and says so when it has none.
 * That happens when the default is removed and several are left - the product
 * will not guess which warehouse is now "the" warehouse, so the panel asks.
 *
 * Country is entered as a two-letter code and the field says so: tax is keyed
 * by it, and "U.A.E." typed in full is refused rather than stored.
 */

type Address = ListAccountAddressesResult['addresses'][number];
type Kind = Address['kind'];
const KINDS: Kind[] = ['billing', 'shipping', 'site', 'branch'];

const EMPTY = { kind: 'billing' as Kind, label: '', line1: '', line2: '', city: '', region: '', postcode: '', country: '' };

export function AccountAddressesPanel({ accountId }: { accountId: string }): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const enabled = useEntitlement('F-028');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(EMPTY);
  const [noDefault, setNoDefault] = useState<Kind | null>(null);

  const key = ['account-addresses', accountId];
  const addresses = useQuery<ListAccountAddressesResult, ApiError>({
    queryKey: key,
    queryFn: () => api.listAccountAddresses({ path: { accountId } }),
    enabled: enabled.enabled,
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: key });

  const add = useMutation({
    mutationFn: () =>
      api.addAccountAddress({
        path: { accountId },
        body: {
          kind: draft.kind,
          label: draft.label || null,
          line1: draft.line1,
          line2: draft.line2 || null,
          city: draft.city || null,
          region: draft.region || null,
          postcode: draft.postcode || null,
          country: draft.country,
        },
      }),
    onSuccess: () => {
      setDraft(EMPTY);
      setAdding(false);
      setNoDefault(null);
      refresh();
    },
  });

  const makeDefault = useMutation({
    mutationFn: (addressId: string) =>
      api.setDefaultAccountAddress({ path: { accountId, addressId }, body: {} }),
    onSuccess: () => {
      setNoDefault(null);
      refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (addressId: string) => api.removeAccountAddress({ path: { accountId, addressId } }),
    onSuccess: (result) => {
      setNoDefault(result.noDefault);
      refresh();
    },
  });

  if (!enabled.enabled || addresses.isPending) return null;
  if (addresses.isError) {
    return (
      <Panel data-region="account-addresses">
        <p className="text-dense text-danger-text">{errorText(addresses.error)}</p>
      </Panel>
    );
  }

  const list = addresses.data.addresses;
  const failure = add.error ?? makeDefault.error ?? remove.error;

  return (
    <Panel data-region="account-addresses">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <SectionLabel>{t('address.title')}</SectionLabel>
          {!adding ? (
            <Button intent="secondary" size="small" data-address-add onClick={() => setAdding(true)}>
              {t('address.add')}
            </Button>
          ) : null}
        </div>

        {noDefault ? (
          <p className="text-dense text-warning-text" data-address-no-default={noDefault}>
            {t('address.noDefault', { values: { kind: t(`address.kind.${noDefault}`) } })}
          </p>
        ) : null}
        {failure ? <p className="text-dense text-danger-text">{errorText(failure as ApiError)}</p> : null}

        {list.length === 0 && !adding ? (
          <p className="text-dense text-ink-meta" data-address-empty>
            {t('address.empty')}
          </p>
        ) : null}

        {KINDS.filter((kind) => list.some((entry) => entry.kind === kind)).map((kind) => (
          <div key={kind} className="flex flex-col gap-1" data-address-kind={kind}>
            <span className="text-meta uppercase tracking-wide text-ink-meta">{t(`address.kind.${kind}`)}</span>
            <ul className="flex flex-col gap-2">
              {list
                .filter((entry) => entry.kind === kind)
                .map((entry) => (
                  <li
                    key={entry.addressId}
                    data-address={entry.addressId}
                    data-address-default={entry.isDefault ? 'true' : undefined}
                    className="flex flex-wrap items-start justify-between gap-2 text-dense"
                  >
                    <span className="flex flex-col">
                      <span className="text-ink">
                        {entry.label ?? entry.line1}
                        {entry.isDefault ? (
                          <span className="ms-2 rounded-chip bg-sunken px-2 text-meta text-ink-600">
                            {t('address.default')}
                          </span>
                        ) : null}
                      </span>
                      <span className="text-ink-meta">
                        {[entry.label ? entry.line1 : null, entry.line2, entry.city, entry.region, entry.postcode, entry.country]
                          .filter(Boolean)
                          .join(', ')}
                      </span>
                    </span>
                    <span className="flex gap-1">
                      {!entry.isDefault ? (
                        <Button
                          intent="secondary"
                          size="small"
                          data-address-make-default={entry.addressId}
                          onClick={() => makeDefault.mutate(entry.addressId)}
                        >
                          {t('address.makeDefault')}
                        </Button>
                      ) : null}
                      <Button
                        intent="secondary"
                        size="small"
                        data-address-remove={entry.addressId}
                        onClick={() => remove.mutate(entry.addressId)}
                      >
                        {t('address.remove')}
                      </Button>
                    </span>
                  </li>
                ))}
            </ul>
          </div>
        ))}

        {adding ? (
          <form
            data-address-form
            className="flex flex-col gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              add.mutate();
            }}
          >
            <label className="flex flex-col gap-1 text-meta text-ink-meta">
              {t('address.kind')}
              <select
                data-address-field="kind"
                value={draft.kind}
                onChange={(event) => setDraft({ ...draft, kind: event.target.value as Kind })}
                className="h-input rounded-control border border-control bg-surface px-2 text-body text-ink"
              >
                {KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {t(`address.kind.${kind}`)}
                  </option>
                ))}
              </select>
            </label>
            <Field label={t('address.label')} data-address-field="label" value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.currentTarget.value })} />
            <Field label={t('address.line1')} data-address-field="line1" value={draft.line1} required
              onChange={(e) => setDraft({ ...draft, line1: e.currentTarget.value })} />
            <Field label={t('address.line2')} data-address-field="line2" value={draft.line2}
              onChange={(e) => setDraft({ ...draft, line2: e.currentTarget.value })} />
            <div className="flex flex-wrap gap-2">
              <Field label={t('address.city')} data-address-field="city" value={draft.city}
                onChange={(e) => setDraft({ ...draft, city: e.currentTarget.value })} />
              <Field label={t('address.region')} data-address-field="region" value={draft.region}
                onChange={(e) => setDraft({ ...draft, region: e.currentTarget.value })} />
              <Field label={t('address.postcode')} data-address-field="postcode" value={draft.postcode}
                onChange={(e) => setDraft({ ...draft, postcode: e.currentTarget.value })} />
            </div>
            {/* Says it wants a code, because tax is keyed by one. */}
            <Field label={t('address.country')} data-address-field="country" value={draft.country} required
              maxLength={2} placeholder="AE"
              onChange={(e) => setDraft({ ...draft, country: e.currentTarget.value })} />
            <div className="flex gap-2">
              <Button intent="primary" size="small" type="submit" data-address-save disabled={add.isPending}>
                {t('address.save')}
              </Button>
              <Button intent="secondary" size="small" type="button" onClick={() => setAdding(false)}>
                {t('address.cancel')}
              </Button>
            </div>
          </form>
        ) : null}
      </div>
    </Panel>
  );
}
